import type { UiHandlerDeps } from "./types.ts";
import { t } from "../../utils/i18n.ts";
import { COLORS } from "../../core/constants.ts";
import {
  FEEL_BEATS,
  TOWER_UPGRADE_FLASH_ALPHA,
} from "../../core/feelBudget.ts";
import { ELEMENT_COLORS, ECHELON } from "../../rendering/designTokens.ts";
import { rerouteAllPaths } from "../../systems/PathRerouter.ts";
import { PRIORITY } from "../../ui/commsContent.ts";

/** Wire UI/breach/firewall EventBus subscriptions. */
export function setupUIHandlers(deps: UiHandlerDeps): (() => void)[] {
  const {
    eventBus,
    gs,
    particleSystem,
    screenEffects,
    economy,
    grid,
    recalculatePath,
    gameBanner,
    synergySystem,
    gridRenderer,
    waveManager,
    pathRegistry,
    scannerReadout,
    commsRouter,
  } = deps;
  const unsubs: (() => void)[] = [];
  /** D-331 — a no-op fallback so a headless caller without `commsRouter`
   *  never throws on the newly-rerouted status/narration lines. */
  const commsStatus = (text: string, priority: number): void => {
    commsRouter?.status(text, priority);
  };

  unsubs.push(
    eventBus.on("firewall.destroyed", (d: unknown) => {
      const ev = d as { firewall: { col: number; row: number; id: string } };
      const idx = gs.firewalls.findIndex((fw) => fw.id === ev.firewall.id);
      if (idx !== -1) {
        gs.firewalls.splice(idx, 1);
      }
      grid.setCell(ev.firewall.col, ev.firewall.row, 0);
      recalculatePath();
      // GA-15 fix: reroute already-spawned enemies onto the freshly-reopened
      // route — mirrors tower/firewall PLACEMENT (placementPorts.ts), which
      // already does this on the ADDITION side of a grid mutation.
      rerouteAllPaths(waveManager, pathRegistry);
      particleSystem.emit(
        ev.firewall.col * 48 + 24,
        ev.firewall.row * 48 + 24,
        "fire",
        15,
      );
      gameBanner.show(t("firewall.destroyed"), COLORS.AMBER_WARN, 1500);
    }),
  );

  unsubs.push(
    eventBus.on("breach.destroyCell", (d: unknown) => {
      const ev = d as { x: number; y: number };
      const cell = grid.getCell(ev.x, ev.y);
      if (!cell) return;

      const cx = ev.x * 48 + 24;
      const cy = ev.y * 48 + 24;

      // C1: refund by tower/firewall MEMBERSHIP, not by current cell type. A
      // tower built on a hidden-spawn trap sits on a cell that is already
      // PATH_ENTRY at eruption, so a `cell.type === TOWER` check skipped the
      // refund AND never removed the ghost tower. Membership keys it correctly
      // for both the on-spawn trap tower and ordinary path-blocker towers.
      const tIdx = gs.towers.findIndex((t) => t.col === ev.x && t.row === ev.y);
      if (tIdx !== -1) {
        const tower = gs.towers[tIdx]!;
        const refund = economy.getSellPrice(tower.totalInvested);
        economy.addGold(refund);
        deps.floatingDmg.spawn(cx, cy - 10, refund, COLORS.AMBER_WARN);
        eventBus.emit("tower.sold", {
          tower,
          sellPrice: refund,
          cause: "breach",
        });
        gs.towers.splice(tIdx, 1);
      } else {
        const fIdx = gs.firewalls.findIndex(
          (fw) => fw.col === ev.x && fw.row === ev.y,
        );
        if (fIdx !== -1) {
          const fw = gs.firewalls[fIdx]!;
          economy.addGold(fw.cost);
          deps.floatingDmg.spawn(cx, cy - 10, fw.cost, COLORS.AMBER_WARN);
          gs.firewalls.splice(fIdx, 1);
        }
      }

      // Clear to EMPTY only if NOT an active path entry/exit — never wipe the
      // freshly-erupted breach spawn cell (that would orphan the entry).
      if (cell.type !== 4 /* PATH_ENTRY */ && cell.type !== 5 /* PATH_EXIT */) {
        grid.setCell(ev.x, ev.y, 0);
      }
      // Synergy graph references the spliced tower's neighbors; rebuild now
      // so any observer firing during the cinematic sees coherent state.
      synergySystem.setTowers(gs.towers);
      synergySystem.recalculate();
      particleSystem.emit(cx, cy, "fire", 20);
      screenEffects.shake(4, 150);
    }),
  );

  // Tower destruction text sync (during path reveal animation). D-331:
  // narration synced to the (non-interactive) path-reveal cinematic — TV,
  // not the grid.
  unsubs.push(
    eventBus.on("breach.towerDestroyed", () => {
      commsStatus(t("breach.towerDestroyed"), PRIORITY.MED);
    }),
  );

  unsubs.push(
    eventBus.on("breach.completed", () => {
      recalculatePath();
      synergySystem.setTowers(gs.towers);
      synergySystem.recalculate();
      gameBanner.show(t("breach.spawnOnline"), COLORS.ORACLE_RED, 2000);
    }),
  );

  unsubs.push(
    eventBus.on("boss.wave.incoming", () => {
      screenEffects.vignette(COLORS.ORACLE_RED, 1500);
      screenEffects.flash(COLORS.ORACLE_RED, 0.25);
      gameBanner.show(t("boss.incoming.banner"), COLORS.ORACLE_RED, 2000);
    }),
  );

  // CR-H3 (LD-22.1-2): Stealth-wave warning — vignette + banner hints player to
  // build the Signal tower (Beacon-Node, cost 75). Colour resolves from the
  // central palette (Beacon's brand) for visual association.
  unsubs.push(
    eventBus.on("wave.stealth.incoming", () => {
      screenEffects.vignette(ELEMENT_COLORS.signal, 1500);
      screenEffects.flash(ELEMENT_COLORS.signal, 0.2);
      gameBanner.show(
        "⚠ STEALTH PROTOCOL DETECTED — DEPLOY SIGNAL",
        ELEMENT_COLORS.signal,
        3000,
      );
    }),
  );

  // S141 / D-236 — Lever 1: the ECHELON mutation telegraph. Her magenta (D-224
  // identity, never ORACLE_RED — that's YOUR alarm) names the mutated thread at
  // wave-start so the player can recompose. Reduced-motion safe (text + the
  // governed flash). The banner copy is i18n (EN+DE parity).
  unsubs.push(
    eventBus.on("wave.mutation.incoming", (d: unknown) => {
      const ev = d as { modifier: string };
      screenEffects.vignette(ECHELON.SIGNAL, 1500);
      screenEffects.flash(ECHELON.SIGNAL, 0.2);
      gameBanner.show(t(`wave.mutation.${ev.modifier}`), ECHELON.SIGNAL, 3000);
    }),
  );

  // BL-152 / BL-153 (S154) — the "NEW THREAT DETECTED" telegraph (fairness
  // invariant 1). When a per-act new archetype (Jammer / Overseer) debuts, her
  // magenta (ECHELON, D-224 — NOT ORACLE_RED, that's the player's own alarm)
  // names the threat AND its counter-play, once per archetype. The banner copy
  // is i18n (EN+DE); reduced-motion / flash-governor safe (text + governed flash).
  unsubs.push(
    eventBus.on("wave.newthreat.incoming", (d: unknown) => {
      const ev = d as { types: string[] };
      for (const type of ev.types) {
        screenEffects.vignette(ECHELON.SIGNAL, 1800);
        screenEffects.flash(ECHELON.SIGNAL, 0.25);
        gameBanner.show(t(`wave.newthreat.${type}`), ECHELON.SIGNAL, 3500);
      }
    }),
  );

  // GA-08 (Spec C+) — INTRUSION_SCANNER's payload finally has a destination.
  //
  // The card promised "Reveals next 3 wave compositions. Plan your build." and
  // emitted `intel.revealWaves` to ZERO subscribers — an exhaustive grep found
  // no consumer and no wave-preview surface anywhere. A player drafting it
  // spent a real pick, saw "SCAN 3 WAVES", and got nothing at all.
  //
  // D-331 (2026-07-31): this originally reused `gameBanner` (the only moment
  // surface that existed then) — but a canvas `fillText` never renders "\n",
  // so the "\n"-joined multi-line body silently ran together on one line.
  // The owner's grid re-route audit called this out: a multi-wave preview is
  // READABLE DATA, not a message — it gets its OWN small surface
  // (`ScannerReadout`), never GameBanner and never the TV chyron (which is
  // capped at 2 lines, D-329's own contract). Compositions come from
  // `previewComposition`, which reports the SCALED counts the player will
  // actually meet — the authored template says 14 stealth daemons at W16
  // where the real number is 18, and a preview that under-reports is worse
  // than none.
  unsubs.push(
    eventBus.on("intel.revealWaves", (d: unknown) => {
      const ev = d as { count: number };
      const from = waveManager.currentWave + 1;
      const lines: string[] = [];
      for (let w = from; w < from + ev.count; w++) {
        const comp = waveManager.previewComposition(w);
        if (comp.length === 0) continue; // past the final wave
        lines.push(
          t("intel.revealWaves.line", {
            n: w,
            comp: comp.map((c) => `${c.count}× ${c.name}`).join(" · "),
          }),
        );
      }
      if (lines.length > 0) {
        scannerReadout.show(t("intel.revealWaves.header"), lines);
      } else {
        scannerReadout.show(t("intel.revealWaves.none"), []);
      }
    }),
  );

  // Signal tower detects a hidden spawn point. D-331: a positive discovery
  // status (visibility gained, nothing new to react to) — TV, not the grid.
  unsubs.push(
    eventBus.on("spawn.detected", (d: unknown) => {
      const ev = d as { col: number; row: number };
      const cx = ev.col * 48 + 24;
      const cy = ev.row * 48 + 24;
      particleSystem.emit(cx, cy, "fire", 25);
      screenEffects.flash(ELEMENT_COLORS.signal, 0.2);
      commsStatus(t("spawn.detected"), PRIORITY.LOW);
      gridRenderer.invalidateCellCaches();
    }),
  );

  // Force-reveal of hidden spawn (wave about to use undetected source)
  unsubs.push(
    eventBus.on("spawn.forceRevealed", (d: unknown) => {
      const ev = d as { col: number; row: number };
      const cx = ev.col * 48 + 24;
      const cy = ev.row * 48 + 24;
      particleSystem.emit(cx, cy, "fire", 20);
      screenEffects.flash(COLORS.ORACLE_RED, 0.3);
      screenEffects.vignette(COLORS.ORACLE_RED, 2000);
      gameBanner.show(t("spawn.forceRevealed"), COLORS.ORACLE_RED, 3000);
      gridRenderer.invalidateCellCaches();
    }),
  );

  // Signal Overload: Signal tower placed directly on HIDDEN_SPAWN (distance==0)
  unsubs.push(
    eventBus.on("signal.overload", (d: unknown) => {
      const ev = d as { col: number; row: number };
      const cx = ev.col * 48 + 24;
      const cy = ev.row * 48 + 24;
      // Massive green shockwave burst
      particleSystem.emit(cx, cy, "signal", 40);
      particleSystem.emit(cx, cy, "fire", 30);
      screenEffects.flash(COLORS.SENTINEL_GREEN, 0.5);
      screenEffects.shake(10, 600);
      screenEffects.vignette(COLORS.SENTINEL_GREEN, 2500);
      // D-331: a celebratory reward beat (nothing left to react to) — TV, not
      // the grid, mirroring how the kernel_panic WOW already lands there.
      commsStatus(t("signal.overload"), PRIORITY.MED);
      gridRenderer.invalidateCellCaches();
    }),
  );

  // S43: upgrade-moment burst (render-side only, no gameplay mutation)
  unsubs.push(
    eventBus.on("tower.upgraded", (d: unknown) => {
      const ev = d as {
        tower: { x: number; y: number; element: string; color: string };
      };
      const t = ev.tower;
      const element = t.element as
        | "fire"
        | "water"
        | "lightning"
        | "signal"
        | "kinetic"
        | "virus";
      particleSystem.emitRing(t.x, t.y, 24, element, 16);
      particleSystem.emitFlash(t.x, t.y);
      // S195/BL-280 (law #3): on the L5→GOLD commit the MomentDirector's
      // goldReached beat owns the screen (amber flash + punch) — the ordinary
      // upgrade flash yields so the pinnacle frame isn't a two-colour fight.
      const goldNow =
        (ev as { tower?: { isGold?: () => boolean } }).tower?.isGold?.() ??
        false;
      if (!goldNow) {
        screenEffects.flash(t.color, TOWER_UPGRADE_FLASH_ALPHA);
      }
      screenEffects.shake(...FEEL_BEATS.towerUpgrade.shake);
    }),
  );

  return unsubs;
}
